import React, { useState, useEffect } from 'react';
import { Paper, TextField, InputAdornment, Tooltip, IconButton } from '@material-ui/core';
import newButton from '@/assets/img/iconos/newButton.svg';
import searchIcon from '@/assets/img/iconos/searchIcon.svg';
import { PaymentMethodsTable } from './components/PaymentMethodsTable';
import { PaymentMethodForm } from './components/PaymentMethodForm';
import { getProductServiceConfigsService } from '@/services';
import { useStyles } from './PaymentMethodsScreen.styles';

export interface PaymentMethodDetail {
  method: string;
  order: number;
  status: 'ACTIVE' | 'INACTIVE';
}

export interface PaymentMethod {
  id: string;
  sk: string | null;
  product_type: string;
  product_name: string;
  terminalType: string; // 'DISPOSITIVO' | 'ECOMMERCE'
  name: string;
  description: string;
  price: number;
  quantity?: number;
  pricingModel: string; // 'ONE_PAY' | 'RENTAL'
  active: boolean;
  paymentMethods: {
    card: boolean;
    paymentButton: boolean;
    transfer: boolean;
  };
  paymentMethodsDetails: PaymentMethodDetail[]; // Detalles completos con status y order
  paymentMethodsOrder: string[]; // e.g. ['card', 'paymentButton', 'transfer']
  features: { value: string }[]; // Array de objetos para useFieldArray
  labels: {
    notRequested: string;
    requested: string;
  };
  createdAt?: string;
  updatedAt?: string;
}

const defaultFormData: Partial<PaymentMethod> = {
  sk: null,
  product_type: 'BILLET_TAP',
  product_name: '',
  terminalType: 'ECOMMERCE',
  name: '',
  description: '',
  price: 0,
  quantity: 1,
  pricingModel: 'ONE_PAY',
  active: true,
  paymentMethods: {
    card: true,
    paymentButton: false,
    transfer: false,
  },
  paymentMethodsDetails: [],
  paymentMethodsOrder: ['card', 'paymentButton', 'transfer'],
  features: [{ value: '' }],
  labels: {
    notRequested: '',
    requested: '',
  },
};

interface PaymentMethodsScreenProps {
  readOnly?: boolean;
}

export const PaymentMethodsScreen: React.FC<PaymentMethodsScreenProps> = ({ readOnly = false }) => {
  const classes = useStyles();
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [enteredFilter, setEnteredFilter] = useState('');
  const [popUpAction, setPopUpAction] = useState<{
    open: boolean;
    title: string;
    data: PaymentMethod | null;
  }>({
    open: false,
    title: '',
    data: null,
  });

  const fetchPaymentMethods = async () => {
    setLoading(true);
    try {
      const res = await getProductServiceConfigsService();
      console.log('API Response:', res);

      // Parse body if it's a string
      let data = res?.data;
      if (data?.body && typeof data.body === 'string') {
        data = JSON.parse(data.body);
      } else if (data?.body) {
        data = data.body;
      }

      console.log('Parsed Data:', data);

      // Transform data to PaymentMethod structure
      const mappedData: PaymentMethod[] = (Array.isArray(data) ? data : []).map((item: any) => {
        const base = item.config?.base || {};
        const pricing = item.config?.pricing || {};

        // Terminal type: convert array to single value or take as is
        const terminalType = Array.isArray(base.type)
          ? base.type[0] || 'ECOMMERCE'
          : base.type || 'ECOMMERCE';

        // Pricing model: convert array to single value or take as is
        const pricingModel = Array.isArray(pricing.model)
          ? pricing.model[0] || 'ONE_PAY'
          : pricing.model || 'ONE_PAY';

        // Features: extract from labels.features or use description
        const features = base.labels?.features || [base.description];

        // Resolve payment methods order from API response
        const methodApiToKey: Record<string, string> = {
          CREDIT_CARD: 'card',
          BHD_BUTTON: 'paymentButton',
          BANK_TRANSFER: 'transfer',
        };
        const supportedMethods = base.acquisition?.payment_methods_supported || [];
        let paymentMethodsOrder = ['card', 'paymentButton', 'transfer'];
        let paymentMethodsDetails: PaymentMethodDetail[] = [];
        
        if (Array.isArray(supportedMethods) && supportedMethods.length > 0 && typeof supportedMethods[0] === 'object') {
          // New format with order: [{ method: 'CREDIT_CARD', order: 1, status: 'ACTIVE' }, ...]
          paymentMethodsDetails = supportedMethods.map((m: any) => ({
            method: m.method,
            order: m.order || 0,
            status: m.status || 'ACTIVE',
          }));
          
          const sorted = [...supportedMethods].sort((a: any, b: any) => (a.order || 0) - (b.order || 0));
          const orderedKeys = sorted
            .map((m: any) => methodApiToKey[m.method])
            .filter(Boolean);
          // Add any missing keys at the end
          const allKeys = ['card', 'paymentButton', 'transfer'];
          const remaining = allKeys.filter(k => !orderedKeys.includes(k));
          paymentMethodsOrder = [...orderedKeys, ...remaining];
        }

        return {
          id: item.sk || base.SK || base.product_type || item.product_name,
          sk: item.sk || base.SK || null,
          product_type: item.product_type || base.product_type || '',
          product_name: item.product_name || '',
          terminalType: terminalType,
          name: item.product_name || '',
          description: base.description || '',
          price: pricing.amount || 0,
          quantity: 1,
          pricingModel: pricingModel,
          active: typeof base.active === 'boolean' ? base.active : (base.status === 'ACTIVE'),
          paymentMethods: {
            card: Array.isArray(supportedMethods) && supportedMethods.length > 0 && typeof supportedMethods[0] === 'object'
              ? supportedMethods.some((m: any) => m.method === 'CREDIT_CARD' && m.status === 'ACTIVE')
              : (supportedMethods as string[])?.includes?.('CREDIT_CARD') || false,
            paymentButton: Array.isArray(supportedMethods) && supportedMethods.length > 0 && typeof supportedMethods[0] === 'object'
              ? supportedMethods.some((m: any) => m.method === 'BHD_BUTTON' && m.status === 'ACTIVE')
              : (supportedMethods as string[])?.includes?.('BHD_BUTTON') || false,
            transfer: Array.isArray(supportedMethods) && supportedMethods.length > 0 && typeof supportedMethods[0] === 'object'
              ? supportedMethods.some((m: any) => m.method === 'BANK_TRANSFER' && m.status === 'ACTIVE')
              : (supportedMethods as string[])?.includes?.('BANK_TRANSFER') || false,
          },
          paymentMethodsDetails,
          paymentMethodsOrder,
          features: features.map((f: string) => ({ value: f })),
          labels: {
            notRequested: base.labels?.not_requested || '',
            requested: base.labels?.requested || '',
          },
        };
      });

      console.log('Mapped Data:', mappedData);
      setPaymentMethods(mappedData);
    } catch (error) {
      console.error('Error fetching payment methods:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePaymentMethod = () => {
    setPopUpAction({
      open: true,
      title: 'Agregar Medio de Pago',
      data: null,
    });
  };

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  return (
    <Paper className={classes.paper}>
        <div>
          <TextField
            className={classes.searchTextField}
            value={enteredFilter}
            onChange={(e) => setEnteredFilter(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <img src={searchIcon} alt="searchIcon" style={{ paddingLeft: '10px' }} />
                </InputAdornment>
              ),
              disableUnderline: true,
            }}
            placeholder="Buscar"
          />
          {!readOnly && (
            <Tooltip title="Agregar método de pago" aria-label="Nuevo método de pago">
              <IconButton
                onClick={handleCreatePaymentMethod}
                style={{ fontSize: '20px', float: 'right' }}
                edge="end"
                size="small"
              >
                <img src={newButton} alt="newButton" />
              </IconButton>
            </Tooltip>
          )}
        </div>

        <PaymentMethodsTable
          paymentMethods={paymentMethods}
          setPaymentMethods={setPaymentMethods}
          enteredFilter={enteredFilter}
          setPopUpAction={setPopUpAction}
          refreshData={fetchPaymentMethods}
          loading={loading}
          readOnly={readOnly}
        />

        <PaymentMethodForm
          key={popUpAction.data?.id || 'new'}
          open={popUpAction.open}
          title={popUpAction.title}
          data={popUpAction.data}
          defaultValues={popUpAction.data || defaultFormData}
          onClose={() => setPopUpAction({ open: false, title: '', data: null })}
          refreshData={fetchPaymentMethods}
          readOnly={readOnly}
        />
    </Paper>
  );
};

export default PaymentMethodsScreen;
